import React from 'react'
import {Box, Flex} from '../../../utils/chakraMonomorphic'

import {Micropost} from '../../../types'
import {MoreActionsMenu} from '../../../components'
import {ItemHyperlink, ItemMedia, ItemText, ItemMeta} from './index'
import {Feature} from '../../../components/Feature/Feature'
import {useFeatureFlag} from '../../../hooks/useFeatureFlag'
import {useSegmentAudienceSelections} from './useSegmentAudienceSelections'
import {SEASONALITY_FLAG} from '../constants'
import copy from '../copy.json'

type Props = {
  micropost: Micropost
  handleEdit: () => void
  handleDelete: () => void
}

// A custom segment always reads as just "Custom Audience"; the filter names the
// author picked are revealed on hover instead of widening the row.
//
// The season branch is keyed off `isSeasonScoped` rather than `seasonID`
// presence: since AB#25433 every post carries the season it was written in, so a
// season alone no longer means the audience was restricted to it. Testing the
// season here would relabel every all-years post as "Everyone <this year>".
function getAudienceLabel(micropost: Micropost): string {
  if (micropost.segmentID) return copy.audienceLabel.custom
  return micropost.isSeasonScoped && micropost.seasonID
    ? `${copy.audienceLabel.everyone} ${micropost.seasonID}`
    : copy.audienceLabel.everyoneAllSeasons
}

const Wrapper: React.FC<React.PropsWithChildren> = ({children}) => (
  <Flex
    data-testid='micropost-item'
    mx={[4, 8]}
    my={[2, 4]}
    shadow='md'
    borderWidth='1px'
    borderRadius={3}
    direction='column'>
    {children}
  </Flex>
)

export const MicropostItem: React.FC<React.PropsWithChildren<Props>> = ({
  micropost,
  handleEdit,
  handleDelete,
}) => {
  // Null unless this post has a segment whose names resolved, so it doubles as
  // the "is there a tooltip" signal.
  const audienceSelections = useSegmentAudienceSelections(
    micropost.segmentID,
    micropost.seasonID,
  )
  const {value: isSeasonalityEnabled} = useFeatureFlag(SEASONALITY_FLAG, false)

  const actionMenuItems = [
    {key: '1', text: 'Edit', handleClick: handleEdit},
    {key: '2', text: 'Delete', handleClick: handleDelete},
  ]

  return (
    <Wrapper>
      <ItemMedia videoURL={micropost.videoURL} imageURL={micropost.imageURL} />
      <Flex p={4} direction='column' position='relative'>
        <Feature name='manage-microposts'>
          <Box position='absolute' top={[2, 4]} right={[2, 4]}>
            <MoreActionsMenu actions={actionMenuItems} />
          </Box>
        </Feature>
        <ItemMeta
          visibleDate={micropost.publishDate || new Date(Date.now())}
          audienceLabel={
            isSeasonalityEnabled ? getAudienceLabel(micropost) : undefined
          }
          audienceDetail={isSeasonalityEnabled ? audienceSelections : undefined}
          notificationSent={micropost.notify}
        />
        <ItemText my={2} text={micropost.content} />
      </Flex>
      <ItemHyperlink
        url={micropost.hyperlinkURL}
        text={micropost.hyperlinkText}
      />
    </Wrapper>
  )
}
